import React from 'react';
import { FilePond, registerPlugin } from 'react-filepond';
import { FilePondErrorDescription, FilePondFile, FilePondServerConfigProps } from 'filepond';
import FilePondImagePreview from 'filepond-plugin-image-preview';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import { InputWrapper } from '@mantine/core';
import { GenericFieldProps } from 'src/utils/forms';
import { useAppState } from 'src/state';
import { AppState } from 'src/state/_types';

registerPlugin(FilePondImagePreview);

const UPLOAD_API_URL = import.meta.env.VITE_UPLOAD_API_URL || ''
const APP_NAME = import.meta.env.VITE_APP_NAME || ''

export const parseHeaders = (headers: Headers): string => {
    let headerStrings: string[] = []
    for (var pair of headers.entries()) {
        headerStrings.push(pair[0] + ': ' + pair[1]);
    }
    return headerStrings.join('\r\n')
}

const generateServerConfig = (schemaId: string, auth: AppState['auth'], profile: AppState['profile'], type?: GenericFieldProps['type'], parentId?: string | null): FilePondServerConfigProps['server'] => {

    let assetPath = [
        APP_NAME.toLowerCase(),
        profile?.tenant_id || '',
        schemaId || '',
        parentId || '',
        type || '',
    ].reduce<string[]>((paths, path) => {
        if (path && path.length) {
            return [...paths, path]
        }
        return paths
    }, [])

    return {
        process: (fieldName, file, metadata, load, error, progress, abort) => {

            const formData = new FormData();
            formData.append(fieldName, file, file.name);
            formData.append("user_id", auth.user_id || '');
            formData.append("tenant_id", profile?.tenant_id || '');
            formData.append("asset_path", assetPath.join('/'));
            console.log(metadata)

            const request = new XMLHttpRequest();
            request.open('POST', `${UPLOAD_API_URL}/process`);

            request.upload.onprogress = (e) => {
                progress(e.lengthComputable, e.loaded, e.total);
            };

            request.onload = function () {
                if (request.status >= 200 && request.status < 300) {
                    load(request.responseText);
                    let resultData = JSON.parse(request.responseText);
                    load(resultData.url)
                } else {
                    error('Error Processing Upload');
                }
            };

            request.send(formData);

            return {
                abort: () => {
                    request.abort();
                    abort();
                },
            };

        },
        revert: (source, load, error) => {

            const requestPayload = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    url: source,
                    user_id: auth.user_id,
                    tenant_id: profile?.tenant_id
                }),
            }

            fetch(`${UPLOAD_API_URL}/revert`, requestPayload)
                .then(() => {
                    load();
                })
                .catch((e) => {
                    error(e);
                });

        },
        load: (source, load, error, progress, abort, headers) => {

            let controller = new AbortController();

            let requestPayload = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    url: source
                }),
                signal: controller.signal
            }

            fetch(`${UPLOAD_API_URL}/load`, requestPayload)
                .then(response => {
                    if (response.body) {

                        let headersString = parseHeaders(response.headers)
                        headers(headersString)

                        const reader = response.body.getReader();

                        const contentLength = parseInt(response.headers.get('Content-Length') || '0')
                        let receivedLength = 0;
                        let chunks: BlobPart[] = []

                        reader.read().then(function processText({ done, value }): any {

                            if (done) {
                                let blob = new Blob(chunks, { type: response.headers.get('Content-Type') || '' });
                                load(blob)
                                return;
                            }

                            if (value) {
                                receivedLength += value.length;
                                progress(true, receivedLength, contentLength);
                                chunks.push(value);
                            }

                            return reader.read().then(processText).catch(e => { error(e) });

                        }).catch(e => { error(e) })

                    } else {
                        error('Error Loading Upload');
                    }
                })
                .catch(e => error(e))

            return {
                abort: () => {
                    controller.abort()
                    abort();
                },
            };

        },
    }
}

export type AssetUploadProps = GenericFieldProps & {
    schemaId: string;
    parentId?: string | null;
    allowMultiple?: boolean;
    maxFiles?: number;
}

export const AssetUpload: React.FC<AssetUploadProps> = ({ id, schemaId, parentId, type, label, onChange, value, error, required, description, disabled, allowMultiple = false, maxFiles = 1, classNames, getValues, setValues }) => {

    const auth = useAppState(state => state.auth)
    const profile = useAppState(state => state.profile)
    const [urls, setUrls] = React.useState<string[]>([])

    const [files, setFiles] = React.useState<any[]>([])

    const initialValueSet = React.useRef(false)

    React.useEffect(() => {
        if (!initialValueSet.current) {
            if (typeof value === 'string' && value.length) {
                if (allowMultiple) {
                    setFiles(value.split(',').map(item => {
                        return {
                            source: item,
                            options: {
                                type: 'local'
                            }
                        }
                    }))
                    setUrls(value.split(',').map(item => item))
                } else {
                    setFiles([
                        {
                            source: value,
                            options: {
                                type: 'local'
                            }
                        }
                    ])
                }
                initialValueSet.current = true;
            }
        }
    }, [value, allowMultiple])

    const onUploaded = (error: FilePondErrorDescription | null, file: FilePondFile) => {
        if (error) {
            console.error('onUploaded', error);
        }
        else if (file.serverId) {
            if (allowMultiple) {
                let newUrls = [...urls, file.serverId]
                setUrls(newUrls)
                onChange(newUrls)
            } else {
                setValues?.('filename', file.serverId.split('/').pop() || file.filename)
                setValues?.('size', file.fileSize.toString())
                setValues?.('filetype', file.fileExtension)
                onChange(file.serverId)
            }
        }
    }

    const onUpdatedFiles = (updatedFiles: FilePondFile[]) => {
        const files = updatedFiles.map((fileItem) => fileItem.file)
        setFiles(files)
        if (!files.length) {
            onChange(null)
            if (allowMultiple) {
                setUrls([])
            }
        }
    }

    let serverConfig = generateServerConfig(schemaId, auth, profile, type, parentId || getValues?.id || '')

    return (
        <InputWrapper
            id={id}
            label={label}
            description={description}
            error={error}
            required={required}
            classNames={classNames}
            styles={{
                root: {
                    marginBottom: 'var(--mantine-spacing-md)'
                },
                error: {
                    fontSize: 'var(--mantine-font-size-xs)',
                    lineHeight: `var(--mantine-font-size-xs)`,
                    marginTop: 1
                }
            }}
        >
            <FilePond
                name="file"
                required={required}
                disabled={disabled}
                allowMultiple={allowMultiple}
                maxFiles={maxFiles}
                server={serverConfig}
                imagePreviewTransparencyIndicator="grid"
                onprocessfile={onUploaded}
                onupdatefiles={onUpdatedFiles}
                allowImagePreview
                files={files}
                labelIdle='<span class="filepond--label-action-desktop">Drag & Drop your files or <span class="filepond--label-action">Browse</span></span> <span class="filepond--label-action-mobile">Select from Device</span></span>'
            />
        </InputWrapper>

    )

}

export const TalentAssetUpload: React.FC<AssetUploadProps> = ({ id, label, onChange, value, error, required, description, disabled, allowMultiple = false, maxFiles = 1, classNames, getValues }) => {

    const auth = useAppState(state => state.auth)
    const profile = useAppState(state => state.profile)
    const { currentItemId: currentTalentId } = useAppState(state => state.users)
    const [urls, setUrls] = React.useState<{filename: string, size: string, filetype: string}[]>([])

    const [files, setFiles] = React.useState<any[]>([])

    const initialValueSet = React.useRef(false)

    React.useEffect(() => {
        if (!initialValueSet.current) {
            if (typeof value === 'string' && value.length) {
                if (!allowMultiple) {
                    setFiles([
                        {
                            source: value,
                            options: {
                                type: 'local'
                            }
                        }
                    ])
                }
            }
            initialValueSet.current = true;
        }
    }, [value, allowMultiple])

    const onUploaded = (error: FilePondErrorDescription | null, file: FilePondFile) => {
        if (error) {
            console.error('onUploaded', error);
        }
        else if (file.serverId) {
            let newUrl = {
                url: file.serverId,
                filename: file.serverId.split('/').pop() || file.filename,
                size: file.fileSize.toString(),
                filetype: file.fileExtension
            }
            let newUrls = [...urls, newUrl]
            setUrls(newUrls)
            onChange(JSON.stringify(newUrls))
        }
    }

    const onUpdatedFiles = (updatedFiles: FilePondFile[]) => {
        const files = updatedFiles.map((fileItem) => fileItem.file)
        setFiles(files)
        if (!files.length) {
            onChange(null)
            if (allowMultiple) {
                setUrls([])
            }
        }
    }

    let enableMultiple = allowMultiple || ['document','media','invoice','press_pic'].includes(getValues?.type || '')
    let allowedMaxFiles = enableMultiple ? 10 : maxFiles

    let serverConfig = generateServerConfig('talent', auth, profile, getValues?.type, currentTalentId)

    return (
        <InputWrapper
            id={id}
            label={label}
            description={description}
            error={error}
            required={required}
            classNames={classNames}
            styles={{
                root: {
                    marginBottom: 'var(--mantine-spacing-md)'
                },
                error: {
                    fontSize: 'var(--mantine-font-size-xs)',
                    lineHeight: `var(--mantine-font-size-xs)`,
                    marginTop: 1
                }
            }}
        >
            <FilePond
                name="file"
                required={required}
                disabled={disabled}
                allowMultiple={enableMultiple}
                maxFiles={allowedMaxFiles}
                server={serverConfig}
                imagePreviewTransparencyIndicator="grid"
                onprocessfile={onUploaded}
                onupdatefiles={onUpdatedFiles}
                allowImagePreview={!enableMultiple}
                files={files}
                labelIdle='<span class="filepond--label-action-desktop">Drag & Drop your files or <span class="filepond--label-action">Browse</span></span> <span class="filepond--label-action-mobile">Select from Device</span></span>'
            />
        </InputWrapper>

    )

}